import { ButtonV2, InputV2 } from '@cimplico/components';
import { UserIcon } from '@heroicons/react/24/outline';
import { Controller, useForm } from 'react-hook-form';
import { useAuthProfileContext } from '../../../../providers/AuthProfileProvider';
import { useCreateProfile } from '../../../../queries/use-create-profile';
import OnboardingSidebar from '../../OnboardingSidebar';
import OnboardingStep from '../../OnboardingStep';
import OnboardingStepPreview from '../../OnboardingStepPreview';

type UserForm = {
    givenName: string;
    familyName: string;
    role: string;
    email: string;
};

function UserFlow(): JSX.Element {
    const profile = useAuthProfileContext();

    const form = useForm<UserForm>({
        defaultValues: {
            givenName: profile.given_name,
            familyName: profile.family_name,
            role: 'Accountant',
        },
    });

    const createUser = useCreateProfile(form.setError);

    function handleSubmit(data: UserForm): void {
        createUser.mutate({ givenName: data.givenName, familyName: data.familyName, role: data.role });
    }

    return (
        <>
            <OnboardingSidebar>
                <OnboardingStepPreview active title="About you" description={'Enter your details'} icon={<UserIcon />} />
            </OnboardingSidebar>

            <OnboardingStep
                title="Tell Us About Yourself"
                description="Please share a few details about yourself so we can personalise your experience."
            >
                <form className="flex flex-col gap-4 max-w-[600px]" {...form} onSubmit={form.handleSubmit(handleSubmit)}>
                    {/* First name */}
                    <Controller
                        name="givenName"
                        control={form.control}
                        rules={{ required: 'A given name is required' }}
                        render={({ field, fieldState }): JSX.Element => {
                            return (
                                <InputV2
                                    type="text"
                                    placeholder="Given name..."
                                    title="Given name"
                                    autoFocus
                                    defaultValue={field.value}
                                    onChange={field.onChange}
                                    errorMessage={fieldState.error?.message}
                                />
                            );
                        }}
                    />
                    {/* Last name */}
                    <Controller
                        name="familyName"
                        control={form.control}
                        rules={{ required: 'A family name is required' }}
                        render={({ field, fieldState }): JSX.Element => {
                            return (
                                <InputV2
                                    type="text"
                                    placeholder="Family name..."
                                    title="Family name"
                                    defaultValue={field.value}
                                    onChange={field.onChange}
                                    errorMessage={fieldState.error?.message}
                                />
                            );
                        }}
                    />
                    <div>
                        <label className="block text-gray-700 font-medium text-sm">Your role</label>
                        <select
                            {...form.register('role')}
                            className="w-full rounded-md border h-[34px] text-sm leading-4 text-gray-800 border-gray-200 ring-0 outline-0 focus:border-primary focus:ring-1 focus:ring-primary"
                        >
                            <option>Accountant</option>
                            <option>Partner</option>
                            <option>Bookkeeper</option>
                            <option>Administration</option>
                            <option>Other</option>
                        </select>
                    </div>
                    {/* Email */}
                    <Controller
                        name="email"
                        control={form.control}
                        render={({ fieldState }): JSX.Element => {
                            return (
                                <InputV2
                                    type="text"
                                    title="Email"
                                    defaultValue={profile.email}
                                    disabled
                                    hint="This cannot be changed at this time"
                                    errorMessage={fieldState.error?.message}
                                />
                            );
                        }}
                    />

                    <ButtonV2
                        label="Create Profile"
                        size="sm"
                        isSubmit
                        isDisabled={createUser.isLoading}
                        isLoading={createUser.isLoading}
                    />
                </form>
            </OnboardingStep>
        </>
    );
}

export default UserFlow;
